import { useMemo, useState } from "react";
import { Check } from "lucide-react";
import {
  euro,
  intervalSuffix,
  priceForInterval,
  usePublicPackages,
  type FeatureDef,
} from "../lib/subscription";

type Interval = "month" | "year";

/**
 * Rein darstellende Paket-Werbeansicht für die öffentlichen Login-/Registrieren-
 * Seiten. Zeigt den echten Paket-Katalog (Name, Preis, wichtigste Leistungen)
 * aus `/api/public/packages` mit Monats-/Jahres-Umschalter — ohne Checkout und
 * ohne nutzerspezifische Infos. Gekauft wird wie bisher erst nach dem Login.
 */
export default function PackagesPromo() {
  const { data, isLoading, isError } = usePublicPackages();
  const [interval, setInterval] = useState<Interval>("month");

  const packages = data?.packages ?? [];
  const featureMap = useMemo(() => {
    const map = new Map<string, FeatureDef>();
    (data?.featureCatalog ?? []).forEach((f) => map.set(f.key, f));
    return map;
  }, [data?.featureCatalog]);

  const hasYearly = packages.some((p) =>
    p.prices.some((pr) => pr.interval === "year"),
  );
  const hasMonthly = packages.some((p) =>
    p.prices.some((pr) => pr.interval === "month"),
  );

  // Nichts anzeigen, solange geladen wird, ein Fehler auftrat oder (noch) keine
  // Pakete buchbar sind — die Login-/Registrieren-Seite bleibt voll nutzbar.
  if (isLoading || isError || packages.length === 0) return null;

  return (
    <div className="px-5 pb-8" data-testid="section-packages-promo">
      <div className="pt-6 border-t border-[var(--color-app-border)]">
        <div className="text-center mb-4">
          <h2 className="text-[18px] font-bold text-[var(--color-app-text)]">
            Unsere Pakete
          </h2>
          <p className="text-[13px] text-[var(--color-app-text-secondary)] mt-1">
            Alle Pre-Flight-Check-Funktionen freischalten. Jederzeit kündbar.
          </p>
        </div>

        {hasMonthly && hasYearly ? (
          <div
            className="seg-track flex items-center justify-center gap-1 p-1 rounded-2xl mb-4"
            data-testid="toggle-promo-interval"
          >
            <button
              type="button"
              onClick={() => setInterval("month")}
              className={`seg-option flex-1 px-4 py-2 rounded-xl text-[14px] font-semibold transition ${
                interval === "month" ? "seg-option-active" : ""
              }`}
              data-testid="button-promo-interval-month"
            >
              Monatlich
            </button>
            <button
              type="button"
              onClick={() => setInterval("year")}
              className={`seg-option flex-1 px-4 py-2 rounded-xl text-[14px] font-semibold transition ${
                interval === "year" ? "seg-option-active" : ""
              }`}
              data-testid="button-promo-interval-year"
            >
              Jährlich
            </button>
          </div>
        ) : null}

        <div className="space-y-3">
          {packages.map((pkg) => {
            const price = priceForInterval(pkg, interval);
            return (
              <div
                key={pkg.id}
                className="card p-4"
                data-testid={`card-promo-package-${pkg.id}`}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="text-[16px] font-bold text-[var(--color-app-text)]">
                    {pkg.name}
                  </h3>
                  {price ? (
                    <div className="text-right shrink-0">
                      <div className="text-[18px] font-bold text-[var(--color-app-text)]">
                        {euro(price.unitAmount, price.currency)}
                      </div>
                      <div className="text-[11px] text-[var(--color-app-text-tertiary)]">
                        {intervalSuffix(price.interval)}
                      </div>
                    </div>
                  ) : null}
                </div>

                {pkg.description ? (
                  <p className="text-[13px] text-[var(--color-app-text-secondary)] mt-1">
                    {pkg.description}
                  </p>
                ) : null}

                {pkg.features.length > 0 ? (
                  <ul className="mt-3 space-y-1.5">
                    {pkg.features.map((fk) => (
                      <li
                        key={fk}
                        className="flex items-start gap-2 text-[13px] text-[var(--color-app-text)]"
                      >
                        <Check
                          size={15}
                          className="text-[var(--color-go,#16a34a)] mt-0.5 shrink-0"
                        />
                        <span>{featureMap.get(fk)?.name ?? fk}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            );
          })}
        </div>

        <p className="text-[12px] text-[var(--color-app-text-tertiary)] text-center mt-4">
          Buchbar nach der Anmeldung.
        </p>
      </div>
    </div>
  );
}
